/**
 * Error Interceptor
 * 
 * Updated to use new session management architecture.
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import { HttpRequest, HttpResponse, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError, switchMap, filter, take, tap } from 'rxjs/operators'

import { AlertService } from '@/services/alert.service'
import { AuthenticationService } from '@/services/authentication.service'
import { ErrorClassifierService, ErrorAction } from '@/services/error-classifier.service'
import { TokenRefreshOrchestrator, RefreshSource } from '@/services/token-refresh-orchestrator.service'
import { RequestQueueService } from '@/services/request-queue.service'
import { SessionHealthMonitor } from '@/services/session-health-monitor.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { NetworkStateService } from '@/services/network-state.service'
import { log, warn, error } from '@/util/logger'

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private alertService: AlertService,
    private authenticationService: AuthenticationService,
    private errorClassifier: ErrorClassifierService,
    private refreshOrchestrator: TokenRefreshOrchestrator,
    private requestQueue: RequestQueueService,
    private healthMonitor: SessionHealthMonitor,
    private sessionState: SessionStateService,
    private networkState: NetworkStateService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestStartTime = Date.now()
    const requestUrl = request.url
    
    return next.handle(request)
      .pipe(
        tap(() => {
          // Record successful request
          const duration = Date.now() - requestStartTime
          log('[ErrorInterceptor] Request succeeded', {
            url: requestUrl,
            method: request.method,
            duration: `${duration}ms`
          })
          this.healthMonitor.recordSuccessfulRequest()
        }),
        catchError(response => {
          const duration = Date.now() - requestStartTime
          // Classify the error
          const classification = this.errorClassifier.classify(response)
          
          log('[ErrorInterceptor] Request failed', {
            url: requestUrl,
            method: request.method,
            status: response?.status,
            error: response?.error?.error || response?.message,
            classification: {
              type: classification.type,
              action: classification.action,
              recoverable: classification.recoverable
            },
            duration: `${duration}ms`
          })
          
          // Record failure
          this.healthMonitor.recordFailure()

          // Don't try to refresh for the refresh endpoint itself
          const isRefreshRequest = request.url.includes('/auth/refresh')
          if (isRefreshRequest) {
            // Refresh endpoint failed - handle based on classification
            if (classification.type === 'refresh_token_expired') {
              error('[ErrorInterceptor] Refresh token expired, logging out')
              this.sessionState.transitionTo(SessionState.Expired, { error: response })
              this.authenticationService.logout()
              location.reload()
            } else {
              // Transient error - let orchestrator handle retries
              warn('[ErrorInterceptor] Refresh endpoint failed', classification)
            }
            return throwError(response)
          }

          // Handle based on error classification
          switch (classification.action) {
            case ErrorAction.RefreshToken:
              // Token expired - use orchestrator to refresh
              return this.handleTokenRefresh(request, next, classification)

            case ErrorAction.Retry:
              // Recoverable error - retry when possible
              if (!this.networkState.isOnline) {
                // Offline: wait for network, then retry the SAME request in place
                // This keeps the original observable "pending" - caller never sees the error
                log('[ErrorInterceptor] Offline, waiting for network to retry', { url: requestUrl })
                return this.networkState.isOnline$.pipe(
                  filter(online => online),
                  take(1),
                  switchMap(() => {
                    log('[ErrorInterceptor] Network restored, retrying request', { url: requestUrl })
                    return next.handle(request)
                  })
                )
              }
              // Online but still failed - use request queue for retry with backoff
              return this.requestQueue.queueRequest(request)

            case ErrorAction.Logout:
              // Unrecoverable auth error - logout
              error('[ErrorInterceptor] Unrecoverable auth error, logging out', classification)
              this.sessionState.transitionTo(SessionState.Expired, { error: response, classification })
              this.authenticationService.logout()
              return throwError(response)

            case ErrorAction.Queue:
              // Queue request for later (e.g., during refresh)
              return this.requestQueue.queueRequest(request)

            case ErrorAction.Propagate:
            default:
              // Propagate error to caller
              return throwError(response)
          }
        })
      )
  }

  private handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
    classification: any
  ): Observable<HttpEvent<any>> {
    log('[ErrorInterceptor] Access token expired, initiating refresh', {
      url: request.url
    })

    // Use orchestrator to handle refresh (handles deduplication automatically)
    return this.refreshOrchestrator
      .requestRefresh(RefreshSource.Interceptor, `Request failed: ${request.url}`)
      .pipe(
        switchMap(user => {
          log(
            '[ErrorInterceptor] Token refresh successful, retrying original request'
          )

          // Get updated token
          const token = user && user.token

          if (token) {
            const newRequest = request.clone({
              setHeaders: { Authorization: `Bearer ${token}` }
            })
            return next.handle(newRequest)
          } else {
            error('[ErrorInterceptor] Token refresh returned no token')
            // Queue request for retry
            return this.requestQueue.queueRequest(request)
          }
        }),
        catchError(err => {
          error('[ErrorInterceptor] Token refresh failed', err)
          // Queue request for retry after refresh completes
          return this.requestQueue.queueRequest(request)
        })
      )
  }
}
